import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiFetch, apiPost } from '../utils/api';
import { FIELD_LABEL_CLASS } from '../constants/inputs';
// 🔻 `formatCurrency` was imported here until 2026-09-20; its only caller on
// this page was the local `CurrencyInput`, which has moved to
// `components/ui/CurrencyInput.js` and imports it there.
import { BTN, BTN_LG } from '../constants/buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import HireForgeEarlyNotice from '../components/HireForgeEarlyNotice';
// THE money input, shared with `pages/BudgetCostCodes.js` since 2026-09-20; it
// was defined in BOTH files before that. See the marker below.
import CurrencyInput from '../components/ui/CurrencyInput';
import { toPayloadAmount, isMalformedAmount } from '../utils/moneyPayload';

// ---------------------------------------------------------------------------
// 🔻 `CurrencyInput` WAS DEFINED HERE UNTIL 2026-09-20 (job-list C10). It was
// the second of two copies and is now `components/ui/CurrencyInput.js`.
//
// 🚨 THIS FORM'S FIELDS CHANGED, AND THE CHANGE IS A BUG FIX. The local copy
// rendered a standalone `£` glyph AND called `formatCurrency` with its default
// symbol, so a populated, unfocused Daily/Weekly Rate read "£ £1,000.00" —
// measured in a real render. The shared component derives `includeSymbol` FROM
// `prefix`, so the glyph and the symbol can never both appear. Two dead details
// went with it: the `placeholder || '£0.00'` blurred default was UNREACHABLE
// (both call sites below pass "0.00", so it never rendered), and the
// `placeholder || '0.00'` focused default meant a caller could not ask for no
// placeholder at all. Placeholders now pass through verbatim.
//
// 🪤 Malformed input reads differently here now, deliberately: "abc" used to
// render "£NaN" and now hands back "abc". See THE MALFORMED-VALUE RULING in the
// component.
// ---------------------------------------------------------------------------

/** The field styling this form's money inputs have always carried. */
const LISTING_MONEY_FIELD = 'w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function MarketplaceCreateListing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  // Form state
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [priceOnApplication, setPriceOnApplication] = useState(false);
  const [dailyRate, setDailyRate] = useState('');
  const [weeklyRate, setWeeklyRate] = useState('');
  const [notes, setNotes] = useState('');
  const [availability, setAvailability] = useState('');
  const [contactMethod, setContactMethod] = useState('both');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // Owner ruling 2026-09-08 — the account contact opt-in. Unchecked by
  // default: this form must never publish the poster's account name, email
  // or phone to a listing's public contact fields without an explicit
  // choice. `accountProfile` caches one `GET /api/users/me` so re-ticking
  // after an untick does not re-fetch.
  //
  // Untick rule (fix round 1, 2026-09-08 — corrects an EARLIER version of
  // this comment/behaviour that clear-to-blank on untick, which in EDIT
  // mode discarded the listing's own SAVED contact values, not just the
  // account ones): `preTickValues` snapshots what the three fields held the
  // MOMENT BEFORE a tick (in create mode that is '', '', ''; in edit mode
  // it is the listing's saved contact_name/email/phone), and
  // `autoFilledValues` snapshots what that tick WROTE. Unticking restores
  // preTickValues into any field that still holds exactly what the tick
  // wrote; a field the user typed over after ticking is left alone. "The
  // tick did this, untick undoes exactly that — back to what was there
  // before" is the one behaviour a user can predict without watching the
  // fields closely, and it is now correct in both modes because
  // preTickValues IS the pre-tick state, not an assumed blank.
  const [useAccountContact, setUseAccountContact] = useState(false);
  const [accountProfile, setAccountProfile] = useState(null);
  const [autoFilledValues, setAutoFilledValues] = useState(null);
  const [preTickValues, setPreTickValues] = useState(null);

  // Images
  const [existingImages, setExistingImages] = useState([]); // edit mode — already saved
  const [newImages, setNewImages] = useState([]);            // File objects pending upload
  const [removedImageIds, setRemovedImageIds] = useState([]); // IDs to delete on save
  const fileInputRef = useRef(null);

  // Async state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // ---------------------------------------------------------------------------
  // Fetch categories + existing listing (edit)
  // ---------------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const catRes = await apiGet('/api/marketplace/categories');
      if (!catRes.ok) throw new Error('Failed to load categories');
      const catData = await catRes.json();
      setCategories(Array.isArray(catData) ? catData : catData.categories || []);

      if (isEdit) {
        const listRes = await apiGet(`/api/marketplace/listings/${id}`);
        if (!listRes.ok) throw new Error('Failed to load listing');
        const listing = await listRes.json();
        const data = listing.listing || listing;

        setTitle(data.title || '');
        setCategoryId(data.category_id ? String(data.category_id) : '');
        setDescription(data.description || '');
        setLocation(data.location || '');
        setPriceOnApplication(Boolean(data.is_poa));
        setDailyRate(data.daily_rate != null ? data.daily_rate : '');
        setWeeklyRate(data.weekly_rate != null ? data.weekly_rate : '');
        setNotes(data.notes || '');
        setAvailability(data.availability_notes || '');
        setContactMethod(data.contact_method || 'both');
        // LISTING CONTACTS ONLY — never fall back to the poster's account
        // (`poster_name`/`poster_email`/`poster_phone`). A fallback here
        // would silently write the account contact into the listing's own
        // fields on the next save, which is the laundering bug this change
        // exists to close. See `useAccountContact` above for the opt-in.
        setContactName(data.contact_name || '');
        setContactEmail(data.contact_email || '');
        setContactPhone(data.contact_phone || '');
        setExistingImages(data.images || []);
      }
      // Create mode: contact fields start EMPTY. No account fetch here —
      // see `useAccountContact` / `handleUseAccountContactToggle`.
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---------------------------------------------------------------------------
  // Account contact opt-in — see the `useAccountContact` declaration above
  // for the untick rule.
  // ---------------------------------------------------------------------------
  async function handleUseAccountContactToggle(checked) {
    setUseAccountContact(checked);
    if (checked) {
      // Snapshot what the fields hold RIGHT NOW, before overwriting them —
      // this is what untick restores. In create mode this is '', '', ''; in
      // edit mode it is the listing's saved contact values.
      const preTick = { name: contactName, email: contactEmail, phone: contactPhone };
      let profile = accountProfile;
      if (!profile) {
        try {
          const profileRes = await apiGet('/api/users/me');
          if (profileRes.ok) {
            profile = await profileRes.json();
            setAccountProfile(profile);
          }
        } catch (_) { /* ignore — leave the fields as they are */ }
      }
      if (profile) {
        const filled = {
          name: profile.display_name || '',
          email: profile.email || '',
          phone: profile.phone || '',
        };
        setContactName(filled.name);
        setContactEmail(filled.email);
        setContactPhone(filled.phone);
        setAutoFilledValues(filled);
        setPreTickValues(preTick);
      }
    } else if (autoFilledValues && preTickValues) {
      setContactName((v) => (v === autoFilledValues.name ? preTickValues.name : v));
      setContactEmail((v) => (v === autoFilledValues.email ? preTickValues.email : v));
      setContactPhone((v) => (v === autoFilledValues.phone ? preTickValues.phone : v));
      setAutoFilledValues(null);
      setPreTickValues(null);
    }
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------
  function validate() {
    const errs = {};
    if (!title.trim()) errs.title = 'Title is required';
    if (!categoryId) errs.category = 'Category is required';
    if (!priceOnApplication) {
      // 🚨 **THE VALIDATOR MUST ASK THE SAME QUESTION THE PAYLOAD BUILDER
      // ANSWERS (2026-09-22).** This read `parseFloat(dailyRate)`, and
      // `parseFloat('1,000')` is **`1`** — finite, non-negative — so a
      // thousand-separator rate PASSED this gate while `toPayloadAmount`, a
      // hundred lines down in the submit body, answered `null` for the same
      // string. The request then left with BOTH rates null and
      // `is_poa: false`, which the server refuses outright — so the user typed
      // a perfectly ordinary `1,000`, got a bare API error, and never saw the
      // inline message this form has for exactly that case.
      // 🔑 The SHAPE, which root `CLAUDE.md` already records for
      // `crewMoneyFormError` and `nullableRate`: **a guard and a builder that
      // coerce differently is a guard testing a value the server will never
      // see.** `Number.isFinite(parseFloat(x))` is not the house rule.
      // 🪤 MALFORMED IS REFUSED ON ITS OWN, not folded into the "at least one
      // rate" test — with one good rate beside it the folded form passes, and
      // the typed figure then vanishes from the payload silently, which is
      // worse than the error it replaced.
      if (isMalformedAmount(dailyRate) || isMalformedAmount(weeklyRate)) {
        errs.rates = 'A rate must be a number — for example 1000, not 1,000';
      } else {
        const dr = toPayloadAmount(dailyRate, null);
        const wr = toPayloadAmount(weeklyRate, null);
        // 🚨 `< 0`, NOT `<= 0`. A £0 rate is a PRICE (owner, 2026-09-20) — a free
        // or loaned item is a real offer, and the Price on Application toggle
        // above is already the separate "ask me" state. While this read `<= 0` the
        // ruling was UNREACHABLE FROM THE UI: the server stored `0` happily and
        // this form refused to send one. A `null` still catches an empty field,
        // which is what "at least one rate" actually means.
        if ((dr === null || dr < 0) && (wr === null || wr < 0)) {
          errs.rates = 'Enter at least one rate, or enable Price on Application';
        }
      }
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ---------------------------------------------------------------------------
  // Image handling
  // ---------------------------------------------------------------------------
  const totalImages = existingImages.filter(i => !removedImageIds.includes(i.id)).length + newImages.length;

  function handleImageSelect(e) {
    const files = Array.from(e.target.files || []);
    const allowed = 5 - totalImages;
    if (allowed <= 0) return;
    setNewImages(prev => [...prev, ...files.slice(0, allowed)]);
    // Reset input so re-selecting the same file works
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleDrop(e) {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    const allowed = 5 - totalImages;
    if (allowed <= 0) return;
    setNewImages(prev => [...prev, ...files.slice(0, allowed)]);
  }

  function removeNewImage(idx) {
    setNewImages(prev => prev.filter((_, i) => i !== idx));
  }

  function removeExistingImage(imgId) {
    setRemovedImageIds(prev => [...prev, imgId]);
  }

  // ---------------------------------------------------------------------------
  // Upload images helper
  // ---------------------------------------------------------------------------
  async function uploadImages(listingId) {
    if (newImages.length === 0) return;
    const formData = new FormData();
    newImages.forEach((file) => {
      formData.append('images', file);
    });
    // apiFetch: do NOT set Content-Type — browser sets multipart boundary automatically
    await apiFetch(`/api/marketplace/listings/${listingId}/images`, {
      method: 'POST',
      body: formData,
    });
  }

  // ---------------------------------------------------------------------------
  // Delete removed images helper (edit mode)
  // ---------------------------------------------------------------------------
  async function deleteRemovedImages(listingId) {
    for (const imgId of removedImageIds) {
      await apiFetch(`/api/marketplace/listings/${listingId}/images/${imgId}`, {
        method: 'DELETE',
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError(null);

    const payload = {
      title: title.trim(),
      category_id: Number(categoryId),
      description: description.trim(),
      location: location.trim(),
      is_poa: priceOnApplication,
      // 💷 `toPayloadAmount`, NEVER `parseFloat` (2026-09-22). Two reasons, and
      // the first is the older house rule: `parseFloat('1,000')` is **1**, a
      // thousandfold under-price the server would otherwise never see as
      // malformed. The second is the 2dp rule — this is the CHOKE POINT that
      // closes every exit from the box (blur, Enter-submit, a never-focused
      // hydrated value), because a payload builder runs whatever the gesture.
      // Blank still means `null` here: a listing may price by day, by week,
      // both, or neither (Price on Application).
      daily_rate: priceOnApplication ? null : toPayloadAmount(dailyRate, null),
      weekly_rate: priceOnApplication ? null : toPayloadAmount(weeklyRate, null),
      notes: notes.trim(),
      availability_notes: availability.trim(),
      contact_method: contactMethod,
      contact_name: contactName.trim(),
      contact_email: contactEmail.trim(),
      contact_phone: contactPhone.trim(),
    };

    try {
      let listingId = id;

      if (isEdit) {
        const res = await apiFetch(`/api/marketplace/listings/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to update listing');
        }
        await deleteRemovedImages(id);
        await uploadImages(id);
      } else {
        const res = await apiPost('/api/marketplace/listings', payload);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to create listing');
        }
        const result = await res.json();
        listingId = result.id || result.listing?.id;
        if (listingId) {
          await uploadImages(listingId);
        }
      }

      navigate(listingId ? `/hireforge/listings/${listingId}` : '/hireforge');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading) return <LoadingSpinner message={isEdit ? 'Loading listing...' : 'Loading...'} />;

  const visibleExisting = existingImages.filter(i => !removedImageIds.includes(i.id));

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Early-version notice — see components/HireForgeEarlyNotice.js. This is
          the surface where a user commits their own property to the feature,
          so it is the one place the notice most needs to be read before an
          action, not after it. It covers the edit route too: this component
          serves both `/listings/new` and `/listings/:id/edit`. */}
      <HireForgeEarlyNotice className="mb-4" />

      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        {isEdit ? 'Edit Listing' : 'Create Listing'}
      </h1>

      {error && <div className="mb-4"><ErrorMessage error={error} /></div>}

      <form onSubmit={handleSubmit}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 space-y-5">

          {/* Title */}
          <div>
            <label htmlFor="listing-title" className={FIELD_LABEL_CLASS}>
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="listing-title"
              type="text"
              className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Arri Alexa Mini LF Kit"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            {fieldErrors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.title}</p>}
          </div>

          {/* Category */}
          <div>
            <label htmlFor="listing-category" className={FIELD_LABEL_CLASS}>
              Category <span className="text-red-500">*</span>
            </label>
            <select
              id="listing-category"
              className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Select a category...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            {fieldErrors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.category}</p>}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="listing-description" className={FIELD_LABEL_CLASS}>
              Description
            </label>
            <textarea
              id="listing-description"
              rows={4}
              className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe the equipment, condition, included accessories..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Location */}
          <div>
            <label htmlFor="listing-location" className={FIELD_LABEL_CLASS}>
              Location
            </label>
            <input
              id="listing-location"
              type="text"
              className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. London, Manchester, Bristol"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          {/* Pricing section */}
          <fieldset className="space-y-4">
            <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">Pricing</legend>

            {/* POA toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                role="switch"
                aria-checked={priceOnApplication}
                tabIndex={0}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${priceOnApplication ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                onClick={() => setPriceOnApplication(!priceOnApplication)}
                onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setPriceOnApplication(!priceOnApplication); }}}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${priceOnApplication ? 'translate-x-6' : 'translate-x-1'}`} />
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300">Price on Application</span>
            </label>

            {!priceOnApplication && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="listing-daily-rate" className={FIELD_LABEL_CLASS}>
                    Daily Rate
                  </label>
                  <CurrencyInput
                    id="listing-daily-rate"
                    name="daily_rate"
                    value={dailyRate}
                    onChange={setDailyRate}
                    placeholder="0.00"
                    className={LISTING_MONEY_FIELD}
                    prefix
                  />
                </div>
                <div>
                  <label htmlFor="listing-weekly-rate" className={FIELD_LABEL_CLASS}>
                    Weekly Rate
                  </label>
                  <CurrencyInput
                    id="listing-weekly-rate"
                    name="weekly_rate"
                    value={weeklyRate}
                    onChange={setWeeklyRate}
                    placeholder="0.00"
                    className={LISTING_MONEY_FIELD}
                    prefix
                  />
                </div>
              </div>
            )}

            {fieldErrors.rates && <p className="text-sm text-red-600 dark:text-red-400">{fieldErrors.rates}</p>}
          </fieldset>

          {/* Notes */}
          <div>
            <label htmlFor="listing-notes" className={FIELD_LABEL_CLASS}>
              Notes
            </label>
            <textarea
              id="listing-notes"
              rows={3}
              className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Transport costs, minimum hire period, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Availability */}
          <div>
            <label htmlFor="listing-availability" className={FIELD_LABEL_CLASS}>
              Availability
            </label>
            <textarea
              id="listing-availability"
              rows={2}
              className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Available from June, weekends only, etc."
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
            />
          </div>

          {/* Contact Details */}
          <div>
            <label className={FIELD_LABEL_CLASS}>Contact Details</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">These details are shown to everyone who can see this listing.</p>
            {/* Checkbox pattern copied from the house dark-mode-compliant convention —
                e.g. Signup.js's `agreeTerms` checkbox (client/src/pages/Signup.js:253-258)
                and PeopleManager.js's capability checkboxes
                (client/src/components/PeopleManager.js:1407-1413): a plain checkbox
                input carries `border-gray-300 dark:border-gray-600` alongside
                `rounded ... text-blue-600 focus:ring-blue-500`. (AdminDashboard.js's
                widget-visibility checkbox, cited here in an earlier pass, omits the
                dark: variant and is the exception, not the rule — CLAUDE.md's "Every
                new Tailwind colour class MUST include a dark: variant" governs.) */}
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={useAccountContact}
                onChange={(e) => handleUseAccountContactToggle(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Use my account contact details</span>
            </label>
            <div className="mb-3">
              <label htmlFor="listing-contact-name" className={FIELD_LABEL_CLASS}>Contact Name</label>
              <input
                id="listing-contact-name"
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="e.g. SFX Hire Ltd, Amy Fletcher"
                className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="listing-contact-email" className={FIELD_LABEL_CLASS}>Email</label>
                <input
                  id="listing-contact-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="e.g. amy@production.com"
                  className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="listing-contact-phone" className={FIELD_LABEL_CLASS}>Phone</label>
                <input
                  id="listing-contact-phone"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="e.g. 07700 900123"
                  className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className={FIELD_LABEL_CLASS}>
              Images {totalImages > 0 && <span className="text-gray-400 dark:text-gray-500 font-normal">({totalImages}/5)</span>}
            </label>

            {totalImages < 5 && (
              <div
                role="button"
                tabIndex={0}
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); fileInputRef.current?.click(); }}}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <svg className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-8m0 0l-3 3m3-3l3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.338-2.32 3.75 3.75 0 013.572 5.095H6.75z" />
                </svg>
                <p className="text-sm text-gray-600 dark:text-gray-400">Click or drag images here</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Up to {5 - totalImages} more image{5 - totalImages !== 1 ? 's' : ''}</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleImageSelect}
            />

            {/* Thumbnails grid */}
            {(visibleExisting.length > 0 || newImages.length > 0) && (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-4">
                {/* Existing images */}
                {visibleExisting.map((img, idx) => {
                  const isPrimary = idx === 0 && newImages.length === 0;
                  return (
                    <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
                      <img
                        src={img.file_path}
                        alt={img.alt || `Image ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {isPrimary && (
                        <span className="absolute top-1 left-1 bg-yellow-400 text-yellow-900 text-xs font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                          Primary
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeExistingImage(img.id)}
                        className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove image"
                      >
                        X
                      </button>
                    </div>
                  );
                })}

                {/* New images (pending upload) */}
                {newImages.map((file, idx) => {
                  const isPrimary = visibleExisting.length === 0 && idx === 0;
                  return (
                    <div key={`new-${idx}`} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                      {isPrimary && (
                        <span className="absolute top-1 left-1 bg-yellow-400 text-yellow-900 text-xs font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                          Primary
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeNewImage(idx)}
                        className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove image"
                      >
                        X
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className={`${isEdit ? BTN_LG.confirm : BTN_LG.primary} w-full justify-center`}
            >
              {saving ? (
                <>
                  <LoadingSpinner size="sm" inline />
                  {isEdit ? 'Saving...' : 'Posting...'}
                </>
              ) : (
                isEdit ? 'Save Changes' : 'Post Listing'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default MarketplaceCreateListing;
